import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismButton, PrismModal } from '@mylighthouse/prism-ember';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { eq, gt } from 'ember-truth-helpers';
import { formatUrl } from 'frontend-utils/utils/url/format-url';
import HotelBudgetForecastExportModalSelectDataTypes from 'lumen/components/hotel/budget-forecast/export-modal/select-data-types';
import HotelBudgetForecastExportModalSelectDateRange from 'lumen/components/hotel/budget-forecast/export-modal/select-date-range';
import type { CustomExportOptions } from 'lumen/controllers/hotel/budget';
import type { BudgetForecastEntry } from 'lumen/models';
import type AjaxService from 'lumen/services/ajax';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import {
  type DataType,
  DATE_GRANULARITY,
  type DateGranularity,
} from 'lumen/utils/constants/budget-forecast-constants';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';

import styles from './export-modal.module.css';

interface HotelBudgetForecastExportModalSignature {
  Args: {
    dataTypes: DataType[];
    downloadCustomExport: (options: CustomExportOptions) => void;
    entry: BudgetForecastEntry;
    exportType: 'custom' | 'finance-system';
    onClose: () => void;
    view: 'budget' | 'forecast';
  };
}

export type ExportOptions = {
  dataTypes: DataType[];
  endDate: Dayjs;
  granularity: DateGranularity;
  startDate: Dayjs;
};

type FinanceExportQueryParams = {
  end_date: string;
  entry_id: string;
  start_date: string;
  subscription_id: string;
};

type Step = 'select-date-range' | 'select-data-types';

const CUSTOM_EXPORT_STEPS: Step[] = ['select-date-range', 'select-data-types'];
const FINANCE_EXPORT_STEPS: Step[] = ['select-date-range'];

export default class HotelBudgetForecastExportModal extends Component<HotelBudgetForecastExportModalSignature> {
  @service declare access: Services['access'];
  @service declare ajax: AjaxService;
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare toast: Services['toast'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked exportOptions: ExportOptions = {
    startDate: dayjs().startOf('year'),
    endDate: dayjs().endOf('year'),
    granularity: DATE_GRANULARITY.DAY,
    dataTypes: [],
  };

  @tracked stepIndex: number = 0;

  financeExportTask = task({ drop: true }, async (): Promise<void> => {
    const { startDate, endDate } = this.exportOptions;
    const { view } = this.args;

    const baseURL =
      view === 'budget'
        ? '/apigateway/v1/app/budgetforecast/budget/finance-export'
        : '/apigateway/v1/app/budgetforecast/forecast/finance-export';

    const params: FinanceExportQueryParams = {
      subscription_id: this.hotelAndMarketService.hotel.id!,
      start_date: startDate.format('YYYY-MM-DD'),
      end_date: endDate.format('YYYY-MM-DD'),
      entry_id: this.args.entry.id!,
    };

    const exportURL = formatUrl(baseURL, params, this.ajax.host);

    try {
      await this.ajax.request(exportURL, {
        method: 'POST',
      });
      this.toast.success(
        view === 'budget'
          ? this.intl.t('budget_forecast.finance_export.budget.success')
          : this.intl.t('budget_forecast.finance_export.forecast.success'),
        {
          showIcon: true,
        },
      );
    } catch (error) {
      this.toast.error(
        view === 'budget'
          ? this.intl.t('budget_forecast.finance_export.budget.failed')
          : this.intl.t('budget_forecast.finance_export.forecast.failed'),
        {
          showIcon: true,
        },
      );
      this.errorTracking.captureException(error);
      throw new SilentTaskError(getErrorMessage(error));
    }

    this.args.onClose();
  });

  get buttonProperties(): { iconAfter?: 'arrow-right'; label: string } {
    if (this.isLastStep) {
      return {
        label: this.intl.t('general.export'),
      };
    }

    return {
      label: this.intl.t('general.next'),
      iconAfter: 'arrow-right' as const,
    };
  }

  get currentStep(): Step | undefined {
    return this.steps[this.stepIndex];
  }

  get isFinanceExportDisabled(): boolean {
    return !this.access.hasSpider;
  }

  get isLastStep(): boolean {
    return this.stepIndex === this.steps.length - 1;
  }

  get modalTitle(): string {
    if (this.args.exportType === 'finance-system') {
      return this.intl.t('budget_forecast.export.send_to_finance_system');
    }

    return `${this.intl.t('general.export_group.export')} - ${this.intl.t('general.custom')}`;
  }

  get steps(): Step[] {
    return this.args.exportType === 'custom'
      ? CUSTOM_EXPORT_STEPS
      : FINANCE_EXPORT_STEPS;
  }

  constructor(
    owner: Owner,
    args: HotelBudgetForecastExportModalSignature['Args'],
  ) {
    super(owner, args);

    this.exportOptions.dataTypes = this.args.dataTypes;
  }

  @action
  goBack(): void {
    this.stepIndex = Math.max(0, this.stepIndex - 1);
  }

  @action
  goNext(): void {
    this.stepIndex = Math.min(this.stepIndex + 1, this.steps.length - 1);
  }

  @action
  handleNextOrConfirmClick(): void {
    if (this.isLastStep) {
      if (this.args.exportType === 'finance-system') {
        this.usageAnalytics.track('Finance System Export Triggered', {
          eventTrigger: 'finance-export-clicked',
        });

        void this.financeExportTask.perform();
        return;
      }

      this.trackExportClicked();
      this.args.downloadCustomExport({
        startDate: this.exportOptions.startDate.format('YYYY-MM-DD'),
        endDate: this.exportOptions.endDate.format('YYYY-MM-DD'),
        granularity: this.exportOptions.granularity,
        view: this.args.view,
        visibleDataTypes: this.exportOptions.dataTypes,
      });
      this.args.onClose();
      return;
    }

    this.goNext();
  }

  @action
  onDataTypesChange(dataTypes: DataType[]): void {
    this.updateExportOptions({ dataTypes });
  }

  @action
  trackExportClicked(): void {
    if (this.args.view === 'budget') {
      this.usageAnalytics.track('Budget Exported', {
        eventTrigger: 'export-clicked',
        exportType: 'Custom',
        dataTypes: this.exportOptions.dataTypes.join(', '),
      });
    } else {
      this.usageAnalytics.track('Forecast Exported', {
        eventTrigger: 'export-clicked',
        exportType: 'Custom',
        dataTypes: this.exportOptions.dataTypes.join(', '),
      });
    }
  }

  @action
  updateExportOptions(options: Partial<ExportOptions>): void {
    this.exportOptions = {
      ...this.exportOptions,
      ...options,
    };
  }

  <template>
    <PrismModal
      @isCloseDisabled={{this.financeExportTask.isRunning}}
      @onClose={{@onClose}}
      @title={{this.modalTitle}}
      class={{styles.export-modal}}
      data-test-id="export-modal"
    >
      <:default>
        <div class="modal--padded modal--small flex flex-col gap-400">
          {{#if (eq this.currentStep "select-date-range")}}
            <HotelBudgetForecastExportModalSelectDateRange
              @exportOptions={{this.exportOptions}}
              @exportType={{@exportType}}
              @onChange={{this.updateExportOptions}}
            />
          {{/if}}

          {{#if (eq this.currentStep "select-data-types")}}
            <HotelBudgetForecastExportModalSelectDataTypes
              @dataTypes={{@dataTypes}}
              @onDataTypesChange={{this.onDataTypesChange}}
              @selectedDataTypes={{this.exportOptions.dataTypes}}
              @view={{@view}}
            />
          {{/if}}
        </div>
      </:default>
      <:footer>
        <div class="mx-500 flex h-full items-center justify-end gap-200">
          {{#if (gt this.stepIndex 0)}}
            <PrismButton
              @buttonType="ghost"
              @iconBefore="arrow-left"
              @label={{t "general.back"}}
              @onClick={{this.goBack}}
              class="mr-auto"
              data-test-id="export-modal-back"
            />
          {{/if}}
          <PrismButton
            @buttonType="secondary"
            @label={{t "general.cancel"}}
            @onClick={{@onClose}}
            data-test-id="export-modal-cancel"
          />
          <PrismButton
            @iconAfter={{this.buttonProperties.iconAfter}}
            @isLoading={{this.financeExportTask.isRunning}}
            @label={{this.buttonProperties.label}}
            @onClick={{this.handleNextOrConfirmClick}}
            data-test-id="export-modal-next-or-confirm"
          />
        </div>
      </:footer>
    </PrismModal>
  </template>
}
